<template>
  <div class="tw-flex tw-flex-col tw-gap-2 tw-pb-6">
    <AttachmentListItem
      v-for="attachment in regularDocuments"
      :key="attachment.document_id"
      :attachment="attachment"
      :changes-are-disabled="changesAreDisabled"
      :preview-height="attachmentPreviewHeight"
      :preview-opened="
        previewExpanded.currentDocumentId === attachment.document_id &&
        previewExpanded.open
      "
      @delete-attachment="handleDeleteAttachment"
    />
    <ExternalLinkCard
      v-for="linkItem in externalLinks"
      :id="linkItem.document_id"
      :key="linkItem.url"
      :allow-delete="true"
      :allow-edit="true"
      :name="linkItem.name"
      :link="linkItem.url || ''"
      @delete="() => handleDeleteAttachment(linkItem)"
      @edit="editExternalLink"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import partition from 'lodash/partition'

import { DocumentLinkType } from '@app/shared/types/sf-data'
import ExternalLinkCard from '@app/application/external-links/components/external-link-card.vue'
import {
  getMainSubheaderHeight,
  getStickyHeaderHeight,
  getSecondarySubheaderHeight,
} from '@app/utils/domHelpers'

import type { Attachment } from './types'
import AttachmentListItem from './attachment-list-item.vue'
import { useSidebarFileAttachmentsStore } from './store/sidebar-file-attachments'

interface Props {
  attachments: Attachment[]
  changesAreDisabled: boolean
}

const { attachments } = defineProps<Props>()

const sidebarRef = ref<HTMLElement | null>(null)

const store = useSidebarFileAttachmentsStore()
const { editExternalLink, closeExistingFilesBrowser } = store
const { previewExpanded } = storeToRefs(store)

const partitionedItems = computed(() =>
  partition(
    attachments,
    (attachment) => attachment.linkType === DocumentLinkType.ExternalLink
  )
)

const regularDocuments = computed(() => partitionedItems.value[1])
const externalLinks = computed(() => partitionedItems.value[0])

onMounted(() => {
  // Recalculate the attachment preview height when the component is mounted
  setAttachmentPreviewHeight()
  window.addEventListener('resize', setAttachmentPreviewHeight)

  const sidebarElement = document.getElementById('right-drawer') as HTMLElement
  sidebarRef.value = sidebarElement
  sidebarElement.style.scrollPaddingTop = getSecondarySubheaderHeight()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', setAttachmentPreviewHeight)

  // Reset all PDFTron previews
  closeExistingFilesBrowser()
})

const attachmentPreviewHeight = ref('0px')

const emit =
  defineEmits<{
    deleteAttachment: [attachment: Attachment]
    editExternalLink: [id: string]
  }>()

function handleDeleteAttachment(attachment: Attachment) {
  emit('deleteAttachment', attachment)
}

async function setAttachmentPreviewHeight() {
  const stickyHeaderHeight = getStickyHeaderHeight()

  const primaryHeaderHeight = getMainSubheaderHeight()

  const secondaryHeaderHeight = getSecondarySubheaderHeight()

  // Hardcoded to 40px
  const attachmentCardHeight = '40px'
  const marginBottom = '8px'

  attachmentPreviewHeight.value = `calc(100vh - ${stickyHeaderHeight} - ${primaryHeaderHeight} - ${secondaryHeaderHeight} - ${attachmentCardHeight} - ${marginBottom})`
}
</script>
